import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";
import { usePhaseTierDefaults, useSetPhaseTierDefault, useSetDefaultTier } from "@/hooks/usePhaseTierDefaults";
import { useProducts } from "@/hooks/useProducts";
import { Star, Package } from "lucide-react";

export function PhaseTierConfigManager() {
  const [selectedPhase, setSelectedPhase] = useState<string>(INSTALLATION_PHASES[0].key);
  const { data: tierDefaults, isLoading } = usePhaseTierDefaults();
  const { products } = useProducts();
  const setTierDefault = useSetPhaseTierDefault();
  const setDefaultTier = useSetDefaultTier();

  const currentPhase = INSTALLATION_PHASES.find(p => p.key === selectedPhase);
  const phaseTierDefaults = tierDefaults?.filter(d => d.phase_key === selectedPhase) || [];
  
  // Get products for current phase
  const phaseProducts = products?.filter(p => 
    p.install_phase === selectedPhase && p.is_active
  ) || [];

  const getTierDefault = (tier: 'good' | 'better' | 'best') => {
    return phaseTierDefaults.find(d => d.tier === tier);
  };

  const handleSetTierProduct = async (tier: 'good' | 'better' | 'best', productId: string) => {
    await setTierDefault.mutateAsync({
      phase_key: selectedPhase,
      tier,
      product_id: productId,
    });
  };

  const handleSetDefaultTier = async (tier: 'good' | 'better' | 'best') => {
    await setDefaultTier.mutateAsync({
      phase_key: selectedPhase,
      tier,
    });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Phase Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Installation Phase</CardTitle>
          <CardDescription>Configure Good/Better/Best products for each phase</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedPhase} onValueChange={setSelectedPhase}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INSTALLATION_PHASES.map(phase => (
                <SelectItem key={phase.key} value={phase.key}>
                  Phase {phase.number}: {phase.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Current Phase Info */}
      {currentPhase && (
        <Card>
          <CardHeader>
            <CardTitle>Phase {currentPhase.number}: {currentPhase.title}</CardTitle>
            <CardDescription>{currentPhase.customerExplanation}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Tier Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(['good', 'better', 'best'] as const).map(tier => {
          const tierDefault = getTierDefault(tier);
          const isDefault = tierDefault?.is_default;
          
          return (
            <Card key={tier} className={tier === 'better' ? 'border-primary' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant={tier === 'good' ? 'secondary' : tier === 'better' ? 'default' : 'outline'}>
                    {tier.toUpperCase()}
                  </Badge>
                  {isDefault && (
                    <Badge variant="outline" className="gap-1">
                      <Star className="h-3 w-3" fill="currentColor" />
                      Default
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-lg capitalize">{tier} System</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Product Selector */}
                <div>
                  <label className="text-sm font-medium mb-2 block">Select Product</label>
                  <Select
                    value={tierDefault?.product_id || ''}
                    onValueChange={(productId) => handleSetTierProduct(tier, productId)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {phaseProducts.map(product => (
                        <SelectItem key={product.id} value={product.id}>
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            <span>{product.name}</span>
                            {product.brand && (
                              <span className="text-xs text-muted-foreground">({product.brand})</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Current Selection Display */}
                {tierDefault && tierDefault.product && (
                  <div className="p-3 bg-muted rounded-lg">
                    {tierDefault.product.image_url && (
                      <img
                        src={tierDefault.product.image_url}
                        alt={tierDefault.product.name}
                        className="w-full h-32 object-cover rounded mb-2"
                      />
                    )}
                    <p className="font-medium text-sm">{tierDefault.product.name}</p>
                    {tierDefault.product.brand && (
                      <p className="text-xs text-muted-foreground">{tierDefault.product.brand}</p>
                    )}
                  </div>
                )}

                {/* Set as Default Button */}
                {tierDefault && !isDefault && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => handleSetDefaultTier(tier)}
                  >
                    <Star className="h-3 w-3 mr-1" />
                    Set as Default Tier
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {phaseProducts.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>No products assigned to this phase yet.</p>
            <p className="text-sm mt-1">Add products in the Price Book and assign them to this phase.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
